import type { OrgChatMessageDeliveryResult } from '@/contexts/org-chat-interface-context';
import type { DesignNavigationDelivery } from './navigation-delivery';

export type NavigationDeliveryEntry = {
  job: DesignNavigationDelivery;
  status: 'pending' | 'failed';
};
type Run = (
  job: DesignNavigationDelivery,
  checkpoint: (job: DesignNavigationDelivery) => void
) => Promise<OrgChatMessageDeliveryResult>;

export const navigationDeliveryKey = (job: DesignNavigationDelivery) =>
  JSON.stringify([job.actorId, job.orgSlug, job.id]);

/** App-owned operations keep their receipt and retry targets when the source unmounts. */
export function createNavigationDeliveryStore(
  run: Run,
  accepted: (job: DesignNavigationDelivery) => void = () => {}
) {
  let entries: NavigationDeliveryEntry[] = [];
  const listeners = new Set<() => void>();
  const inFlight = new Map<string, Promise<OrgChatMessageDeliveryResult>>();
  const emit = () => {
    for (const listener of listeners) listener();
  };
  const put = (entry: NavigationDeliveryEntry) => {
    entries = [
      ...entries.filter(
        (item) =>
          navigationDeliveryKey(item.job) !== navigationDeliveryKey(entry.job)
      ),
      structuredClone(entry),
    ];
    emit();
  };
  const start = (
    job: DesignNavigationDelivery
  ): Promise<OrgChatMessageDeliveryResult> => {
    const key = navigationDeliveryKey(job);
    const running = inFlight.get(key);
    if (running) return running;
    const snapshot = structuredClone(job);
    put({ job: snapshot, status: 'pending' });
    const delivery = Promise.resolve()
      .then(() =>
        run(snapshot, (checkpoint) =>
          put({ job: checkpoint, status: 'pending' })
        )
      )
      .then(
        (receipt) => {
          if (receipt.accepted) {
            accepted(snapshot);
            entries = entries.filter(
              (item) => navigationDeliveryKey(item.job) !== key
            );
            emit();
          } else {
            put({
              job: {
                ...snapshot,
                options: receipt.retryOptions ?? snapshot.options,
              },
              status: 'failed',
            });
          }
          return receipt;
        },
        () => {
          put({ job: snapshot, status: 'failed' });
          return { status: 'started' as const, accepted: false };
        }
      )
      .finally(() => {
        inFlight.delete(key);
      });
    inFlight.set(key, delivery);
    return delivery;
  };
  return {
    start,
    getSnapshot: () => entries,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    restoreFailures: (restored: NavigationDeliveryEntry[]) => {
      for (const entry of restored) {
        if (
          entry.status === 'failed' &&
          !entries.some(
            (item) =>
              navigationDeliveryKey(item.job) ===
              navigationDeliveryKey(entry.job)
          )
        )
          put(entry);
      }
    },
    retry: (id: string, actorId: string, orgSlug: string) => {
      const entry = entries.find(
        (item) =>
          item.job.id === id &&
          item.job.actorId === actorId &&
          item.job.orgSlug === orgSlug
      );
      if (!entry)
        return Promise.resolve({ status: 'started' as const, accepted: false });
      return start(entry.job);
    },
  };
}
